import { action, computed } from 'mobx';

import { RouterStore } from '@estee/elc-router';
import { ViewController } from '@estee/elc-service';

import {
    AccountAccessPageTabId,
    AccountSignInSignUpSearchParams,
    accountSignUpFormConfigName,
    IamType
} from '~constants/Constants';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { UserRepository } from '~repositories/UserRepository';
import {
    ACCOUNT_GUEST_CHECKOUT_ROUTE,
    ACCOUNT_SIGN_IN_ROUTE,
    ACCOUNT_SIGN_UP_ROUTE
} from '~routes/routes';
import { ConfigurationLoadedUseCase } from '~use-cases/ConfigurationLoadedUseCase';
import { getSocialProviderFromStorage } from '~utils/SocialUtils';
import {
    emitAnalyticsEvent,
    eventTag
} from '~utils/Analytics';
interface IAccountAccessPageViewController {
    userRepository: UserRepository;
    configRepository: ConfigRepository;
    routerStore: RouterStore;
}

export class AccountAccessPageViewController extends ViewController {
    private socialBUIDNode: string;
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private routerStore: RouterStore;
    private routeMaps: Map<
        AccountAccessPageTabId,
        | typeof ACCOUNT_SIGN_IN_ROUTE
        | typeof ACCOUNT_SIGN_UP_ROUTE
        | typeof ACCOUNT_GUEST_CHECKOUT_ROUTE
    > = new Map([
        [AccountAccessPageTabId.SIGN_IN, ACCOUNT_SIGN_IN_ROUTE],
        [AccountAccessPageTabId.SIGN_UP, ACCOUNT_SIGN_UP_ROUTE],
        [AccountAccessPageTabId.GUEST_CHECKOUT, ACCOUNT_GUEST_CHECKOUT_ROUTE]
    ]);

    constructor({
        userRepository,
        configRepository,
        routerStore
    }: IAccountAccessPageViewController) {
        super();
        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.routerStore = routerStore;
        this.socialBUIDNode = `ramp.${getSocialProviderFromStorage()}SocialSignUp`;
    }

    @computed
    public get data() {
        const { isCheckoutFlow, isGuestAndSignIn, iamType } = this.configRepository;
        const {
            consentForm: { isSocialSignUpFlow },
            otpStore: { data: { showOtp } = {} },
            signInWithOtpFormStore: { view } = {}
        } = this.userRepository;
        const currentRoute = this.routerStore.currentRoute;
        let currentTabId = ACCOUNT_SIGN_IN_ROUTE;
        for (const [tabId, route] of this.routeMaps.entries()) {
            if (route === currentRoute) {
                currentTabId = tabId;
                break;
            }
        }

        return {
            showOtp,
            currentTabId,
            isSocialSignUpFlow,
            isCheckoutFlow,
            isGuestAndSignIn,
            view,
            showSignInFormOnly: iamType === IamType.IN_STORE
        };
    }

    @action
    public onSelectTab = (currentTabId: AccountAccessPageTabId) => {
        const route = this.routeMaps.get(currentTabId);
        const { isGuestAndSignIn } = this.configRepository;
        if (route) {
            this.routerStore.changeCurrentRoute(route);
            const queryParams = new URLSearchParams(document.location.search);
            if (isGuestAndSignIn) {
                queryParams.set(AccountSignInSignUpSearchParams.IS_GUEST, '1');
            }
            const queryString = queryParams.toString() ? `?${queryParams}` : '';
            history.pushState(null, currentTabId, `/${route}${queryString}`);
            this.configRepository.setIamType(route);

            // only for the case on sign in to connect for complete user
            if (this.userRepository.isSignInToConnect) {
                if (route === ACCOUNT_SIGN_UP_ROUTE) {
                    new ConfigurationLoadedUseCase(accountSignUpFormConfigName, false).execute();
                }
                if (route === ACCOUNT_SIGN_IN_ROUTE) {
                    new ConfigurationLoadedUseCase(this.socialBUIDNode, false).execute();
                }
            }
            emitAnalyticsEvent(eventTag.ACCOUNT_ROUTE, {route});
        }
    };
}
